import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, combineLatest, delay, map, Subscription, TeardownLogic } from 'rxjs';
import { SessionStorage } from 'ngx-webstorage';

import { ConfigService } from 'services/config/config.service';
import {
    ApplicantAddressInfo,
    ApplicantInfo,
    ApplicantPacket,
    ApplicantPhoneInfo,
    ApplicantTypeInfo,
    ApplicantTypeUpdateResult,
    ApplicationStatus,
    ApplicationSaveParameters,
    ApplicationUpsertResult,
} from './applicant.service.class';
import { ApplicantJobFairInfo } from 'services/jobfair/jobfair.service.class';
import { ReferenceInfo, ReferencesUpdateResult } from 'modules/application/references/services/references.service.class';
import { UserService } from 'services/user/user.service';

import { PhonePipe } from 'modules/pipes';
import { Nullable } from 'classes/nullable';
import { WorkHistoryUpdateResult } from 'modules/application/employment-history/services/employment-history.service.class';
import { SurveyUpdateResult } from 'modules/application/applicant-survey/services/applicant-survey.service.class';

@Injectable({
    providedIn: 'root',
})
export class ApplicantService {
    private _subrelease = new Subscription();
    set subrelease(sub: TeardownLogic) {
        this._subrelease.add(sub);
    }

    private _Application = new BehaviorSubject<ApplicantInfo | null>(null);
    get Application() {
        return this._Application.asObservable();
    }

    private _ApplicationStatus = new BehaviorSubject<ApplicationStatus | null>(null);
    get ApplicationStatus() {
        return this._ApplicationStatus.asObservable();
    }

    constructor(
        private configSvc: ConfigService,
        private http: HttpClient,
        private phonePipe: PhonePipe,
        private userSvc: UserService
    ) {
        // if (this._Application.value == null && !!this.userSvc.User?.AuthToken) {
        //     // This may be a page refresh. In that case, attempt to get the application.
        //     this.RefreshApplication();
        // }
    }

    // #region Get Applicant Address List

    GetApplicantAddressList() {
        return this.http.get<ApplicantAddressInfo[]>(this.configSvc.hostAddress + `api/Applicant/addresses`);
    }

    // #endregion

    // #region Get Applicant Job Fair Status

    GetApplicantJobFairStatus() {
        return this.http.get<ApplicantJobFairInfo[]>(this.configSvc.hostAddress + `api/JobFair/applicant`).pipe(
            map((data) => {
                data?.forEach((d) => {
                    d.RecordCreated = d.RecordCreated ? new Date(d.RecordCreated) : null;
                    d.LastUpdate = d.LastUpdate ? new Date(d.LastUpdate) : null;
                    d.JobFairStart = d.JobFairStart ? new Date(d.JobFairStart) : null;
                    d.JobFairEnd = d.JobFairEnd ? new Date(d.JobFairEnd) : null;
                    d.ApplyStart = d.ApplyStart ? new Date(d.ApplyStart) : null;
                    d.ApplyEnd = d.ApplyEnd ? new Date(d.ApplyEnd) : null;
                    d.WithdrawStart = d.WithdrawStart ? new Date(d.WithdrawStart) : null;
                    d.WithdrawEnd = d.WithdrawEnd ? new Date(d.WithdrawEnd) : null;
                });

                return data;
            })
        );
    }

    // #endregion

    // #region Get Applicant Phone Numbers

    GetApplicantPhoneNumbers() {
        return this.http.get<ApplicantPhoneInfo | null>(this.configSvc.hostAddress + `api/Applicant/phones`).pipe(
            map((data) => {
                if (data?.PrimaryPhone) {
                    data.PrimaryPhone = this.phonePipe.transform(data.PrimaryPhone, data.PrimaryPhoneType === 'I');
                }

                if (data?.SecondaryPhone) {
                    data.SecondaryPhone = this.phonePipe.transform(data.SecondaryPhone, data.SecondaryPhoneType === 'I');
                }

                return data;
            })
        );
    }

    // #endregion

    // #region Get Applicant Type

    GetApplicantType() {
        return this.http.get<ApplicantTypeInfo[]>(this.configSvc.hostAddress + `api/Applicant/type`);
    }

    // #endregion

    // #region Refresh Application

    RefreshApplication() {
        combineLatest([
            this.http.get<ApplicantPacket>(this.configSvc.hostAddress + `api/Applicant`),
            this.http.get<ApplicationStatus>(this.configSvc.hostAddress + `api/Applicant/status`),
        ])
            .pipe(
                map((packet) => {
                    let data = packet[0];
                    let status = packet[1];

                    if (data?.Application) {
                        data.Application.DateOfBirth = data.Application.DateOfBirth ? new Date(data.Application.DateOfBirth) : null;
                        data.Application.DateSubmitted = data.Application.DateSubmitted
                            ? new Date(data.Application.DateSubmitted)
                            : null;
                        data.Application.DateUpdated = data.Application.DateUpdated ? new Date(data.Application.DateUpdated) : null;

                        if (data.Application.PrimaryPhone) {
                            data.Application.PrimaryPhone = this.phonePipe.transform(
                                data.Application.PrimaryPhone,
                                data.Application.PrimaryPhoneType === 'I'
                            );
                        }

                        if (data.Application.SecondaryPhone) {
                            data.Application.SecondaryPhone = this.phonePipe.transform(
                                data.Application.SecondaryPhone,
                                data.Application.SecondaryPhoneType === 'I'
                            );
                        }

                        if (data.Application.WorkHistory) {
                            data.Application.WorkHistory.WorkItems?.forEach((d) => {
                                d.FromDate = d.FromDate ? new Date(d.FromDate) : null;
                                d.ToDate = d.ToDate ? new Date(d.ToDate) : null;
                            });
                        }
                    }

                    // If the result is non-null, and the current stored value is null, update it.
                    if (data?.Application != null && this._Application.value == null) {
                        this._Application.next(data.Application);
                    }

                    // Same for status
                    if (status != null && this._ApplicationStatus.value == null) {
                        this._ApplicationStatus.next(status);
                    }

                    return data?.Application || null;
                })
            )
            .subscribe();
    }

    // #endregion

    // #region Update Application

    UpdateApplication(app: ApplicantInfo | null) {
        this._Application.next(app);
    }

    // #endregion

    // #region Update Application Phones

    UpdateApplicationPhones(phones: ApplicantPhoneInfo | null) {
        if (!phones) return;

        // Make a deep copy of the application, update the phones info, and then reset the property so all subscribers get updated.
        const newapp = JSON.parse(JSON.stringify(this._Application.value)) as ApplicantInfo;
        newapp.PrimaryPhone = phones.PrimaryPhone;
        newapp.PrimaryPhoneType = phones.PrimaryPhoneType;
        newapp.SecondaryPhone = phones.SecondaryPhone;
        newapp.SecondaryPhoneType = phones.SecondaryPhoneType;

        // Advance the app.
        this.UpdateApplication(newapp);
    }

    // #endregion

    // #region Update Application References

    UpdateApplicationReferences(info: ReferencesUpdateResult) {
        // Begin by making a deep copy of the application.
        const newapp = JSON.parse(JSON.stringify(this._Application.value)) as ApplicantInfo;

        // Update the application.
        newapp.References = info.References;
        newapp.AppAvailable = info.Status?.AppAvailable ?? newapp.AppAvailable;
        newapp.AppProgress = info.Status?.AppProgress ?? newapp.AppProgress;
        newapp.AppRequired = info.Status?.AppRequired ?? newapp.AppRequired;
        newapp.HasUnsubmittedChanges = info.Status?.HasUnsubmittedChanges ?? newapp.HasUnsubmittedChanges;

        this.UpdateApplication(newapp);
        this.UpdateApplicationStatuses(info.Status);
    }

    // #endregion

    // #region Update Application Statuses

    UpdateApplicationStatuses(packet: ApplicationStatus | null) {
        // Signal the update to all listeners.
        this._ApplicationStatus.next(packet);
    }

    // #endregion

    // #region Update Application Survey

    UpdateApplicationSurvey(info: SurveyUpdateResult) {
        // Begin by making a deep copy of the application.
        const newapp = JSON.parse(JSON.stringify(this._Application.value)) as ApplicantInfo;

        // Update the application.
        newapp.Survey = info.Survey;
        newapp.AppAvailable = info.Status?.AppAvailable ?? newapp.AppAvailable;
        newapp.AppProgress = info.Status?.AppProgress ?? newapp.AppProgress;
        newapp.AppRequired = info.Status?.AppRequired ?? newapp.AppRequired;
        newapp.HasUnsubmittedChanges = info.Status?.HasUnsubmittedChanges ?? newapp.HasUnsubmittedChanges;

        this.UpdateApplication(newapp);
        this.UpdateApplicationStatuses(info.Status);
    }

    // #endregion

    // #region Update Application Types

    UpdateApplicationTypes(appTypes: Nullable<string[]>, referredBySAPID: Nullable<string>) {
        return this.http
            .post<ApplicantTypeUpdateResult>(this.configSvc.hostAddress + `api/Applicant/type`, {
                ApplicationTypes: appTypes || [],
                ReferredBySAPID: referredBySAPID || '',
            })
            .pipe(
                map((data) => {
                    // Begin by making a deep copy of the application.
                    const newapp = JSON.parse(JSON.stringify(this._Application.value)) as ApplicantInfo;

                    // Update the application.
                    newapp.AppAvailable = data?.Status?.AppAvailable ?? newapp.AppAvailable;
                    newapp.AppProgress = data?.Status?.AppProgress ?? newapp.AppProgress;
                    newapp.AppRequired = data?.Status?.AppRequired ?? newapp.AppRequired;
                    newapp.HasUnsubmittedChanges = data?.Status?.HasUnsubmittedChanges ?? newapp.HasUnsubmittedChanges;
                    newapp.ApplicationTypes = data?.ApplicationTypes || [];
                    newapp.IsTeacherTransfer = data?.IsTeacherTransfer ?? newapp.IsTeacherTransfer;
                    newapp.ReferredByName =
                        !!data?.ReferredByFirstName && !!data?.ReferredByLastName
                            ? `${data.ReferredByFirstName} ${data.ReferredByLastName}`
                            : null;
                    newapp.ReferredBySAPID = data?.ReferredBySAPID ?? null;

                    this.UpdateApplication(newapp);
                    this.UpdateApplicationStatuses(data.Status);

                    return data;
                })
            );
    }

    // #endregion

    // #region Update Application Work History

    UpdateApplicationWorkHistory(info: WorkHistoryUpdateResult) {
        // Begin by making a deep copy of the application.
        const newapp = JSON.parse(JSON.stringify(this._Application.value)) as ApplicantInfo;

        // Update the application.
        newapp.WorkHistory = info.History;
        newapp.AppAvailable = info.Status?.AppAvailable ?? newapp.AppAvailable;
        newapp.AppProgress = info.Status?.AppProgress ?? newapp.AppProgress;
        newapp.AppRequired = info.Status?.AppRequired ?? newapp.AppRequired;
        newapp.HasUnsubmittedChanges = info.Status?.HasUnsubmittedChanges ?? newapp.HasUnsubmittedChanges;

        this.UpdateApplication(newapp);
        this.UpdateApplicationStatuses(info.Status);
    }

    // #endregion

    // #region Upsert Application

    UpsertApplication(packet: ApplicationSaveParameters) {
        return this.http.post<ApplicationUpsertResult>(this.configSvc.hostAddress + `api/Applicant`, packet).pipe(
            map((data) => {
                if (data?.AppPacket?.Application) {
                    data.AppPacket.Application.DateOfBirth = data.AppPacket.Application.DateOfBirth
                        ? new Date(data.AppPacket.Application.DateOfBirth)
                        : null;
                    data.AppPacket.Application.DateSubmitted = data.AppPacket.Application.DateSubmitted
                        ? new Date(data.AppPacket.Application.DateSubmitted)
                        : null;
                    data.AppPacket.Application.DateUpdated = data.AppPacket.Application.DateUpdated
                        ? new Date(data.AppPacket.Application.DateUpdated)
                        : null;

                    if (data.AppPacket.Application.PrimaryPhone) {
                        data.AppPacket.Application.PrimaryPhone = this.phonePipe.transform(
                            data.AppPacket.Application.PrimaryPhone,
                            data.AppPacket.Application.PrimaryPhoneType === 'I'
                        );
                    }

                    if (data.AppPacket.Application.SecondaryPhone) {
                        data.AppPacket.Application.SecondaryPhone = this.phonePipe.transform(
                            data.AppPacket.Application.SecondaryPhone,
                            data.AppPacket.Application.SecondaryPhoneType === 'I'
                        );
                    }
                }

                // If the result is non-null, update the stored application.
                if (data?.AppPacket?.Application != null) {
                    this._Application.next(data.AppPacket.Application);
                }

                this._ApplicationStatus.next(data.Status);

                return data?.AppPacket?.Application || null;
            })
        );
    }

    // #endregion
}
